import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  Eye,
  EyeOff,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import type { Field, Project } from "@proof/core";
import {
  addSection,
  addTab,
  deleteSection,
  fieldsInSection,
  layoutColumns,
  moveField,
  moveSection,
  removedFields,
  sectionsInColumn,
  setSectionInTab,
  ungroupedFields,
  type FieldPlacement,
} from "./form-layout";
import { fieldTypeName } from "./field-types";
import {
  generateFormLayout,
  formLayoutSignature,
  restoreFormLayout,
} from "./generate-form-layout";

type Drag = { kind: "field" | "section"; id: string };
// Containers: "section:<id>", "ungrouped", "removed" (fields); "column:<n>" (sections).
type Target = { container: string; index: number };

/** Click-to-edit text: reads as plain text until someone wants to change it. */
export function InlineText({
  value,
  label,
  placeholder,
  allowEmpty = false,
  className = "",
  onChange,
}: {
  value: string;
  label: string;
  placeholder?: string;
  allowEmpty?: boolean;
  className?: string;
  onChange: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false),
    [text, setText] = useState(value);
  const commit = () => {
    setEditing(false);
    if (text !== value && (allowEmpty || text.trim())) onChange(text);
  };
  if (editing)
    return (
      <input
        autoFocus
        className={`inline-text-input ${className}`}
        aria-label={label}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setText(value);
            setEditing(false);
          }
        }}
      />
    );
  return (
    <button
      type="button"
      className={`inline-text ${value ? "" : "empty"} ${className}`}
      aria-label={`Edit ${label}`}
      title="Click to edit"
      onClick={() => {
        setText(value);
        setEditing(true);
      }}
    >
      <span>{value || placeholder}</span>
      <Pencil size={11} aria-hidden="true" />
    </button>
  );
}

/**
 * Visual form layout: drag sections between columns and fields between
 * sections, rename anything in place, and hide or remove controls.
 */
export function FormLayoutBuilder({
  project: p,
  onChange,
}: {
  project: Project;
  onChange: (project: Project) => void;
}) {
  const [drag, setDrag] = useState<Drag>(),
    [target, setTarget] = useState<Target>(),
    [tabId, setTabId] = useState("");
  const [generated, setGenerated] = useState<{
    before: Project;
    signature: string;
  }>();
  const canUndoGenerated =
    generated &&
    generated.before.id === p.id &&
    generated.signature === formLayoutSignature(p);
  // Keyboard moves can remount a handle; focus follows it to its new place.
  const focusHandle = useRef("");
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focusHandle.current) return;
    root.current
      ?.querySelector<HTMLElement>(
        `[data-handle="${CSS.escape(focusHandle.current)}"]`,
      )
      ?.focus();
    focusHandle.current = "";
  });
  const tab = p.tabs.find((t) => t.id === tabId);
  // On a tab, render only the columns its sections occupy (evenly split).
  const tabColumns = tab
    ? layoutColumns.filter((column) =>
        sectionsInColumn(p, column).some((g) => tab.groupIds.includes(g.id)),
      )
    : [];
  const visibleColumns: number[] = tabColumns.length
    ? tabColumns
    : [...layoutColumns];
  const outsideTab = tab
    ? layoutColumns.flatMap((column) =>
        sectionsInColumn(p, column).filter((g) => !tab.groupIds.includes(g.id)),
      )
    : [];
  const hasSections = p.groups.length > 0;
  const updateField = (id: string, patch: Partial<Field>) =>
    onChange({
      ...p,
      fields: p.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });
  const updateGroup = (id: string, patch: Partial<Project["groups"][number]>) =>
    onChange({
      ...p,
      groups: p.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    });
  const placeField = (id: string, placement: FieldPlacement) =>
    onChange(moveField(p, id, placement));
  const containerFields = (container: string) =>
    container === "ungrouped"
      ? ungroupedFields(p)
      : container === "removed"
        ? removedFields(p)
        : fieldsInSection(p, container.slice("section:".length));
  const fieldContainer = (field: Field) =>
    field.formDisplay === "remove"
      ? "removed"
      : field.groupId && p.groups.some((g) => g.id === field.groupId)
        ? `section:${field.groupId}`
        : "ungrouped";
  const finish = () => {
    setDrag(undefined);
    setTarget(undefined);
  };
  const drop = () => {
    if (!drag || !target) return finish();
    if (drag.kind === "field") {
      // Indexes count the dragged field; placement indexes do not.
      const list = containerFields(target.container),
        from = list.findIndex((f) => f.id === drag.id),
        index =
          from >= 0 && from < target.index ? target.index - 1 : target.index;
      if (target.container === "removed")
        placeField(drag.id, { kind: "removed" });
      else if (target.container === "ungrouped")
        placeField(drag.id, { kind: "ungrouped", index });
      else if (target.container.startsWith("section:"))
        placeField(drag.id, {
          kind: "section",
          groupId: target.container.slice("section:".length),
          index,
        });
    } else if (target.container.startsWith("column:")) {
      const column = Number(target.container.slice("column:".length)),
        list = sectionsInColumn(p, column),
        from = list.findIndex((g) => g.id === drag.id);
      onChange(
        moveSection(
          p,
          drag.id,
          column,
          from >= 0 && from < target.index ? target.index - 1 : target.index,
        ),
      );
    }
    finish();
  };
  const start = (kind: Drag["kind"], id: string) => (e: DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
    setDrag({ kind, id });
  };
  // Hovering an item targets the gap above or below it.
  const overItem =
    (kind: Drag["kind"], container: string, index: number) =>
    (e: DragEvent<HTMLElement>) => {
      if (drag?.kind !== kind) return;
      e.preventDefault();
      e.stopPropagation();
      const box = e.currentTarget.getBoundingClientRect();
      const after = e.clientY > box.top + box.height / 2;
      const next = { container, index: index + (after ? 1 : 0) };
      if (target?.container !== next.container || target.index !== next.index)
        setTarget(next);
    };
  const overContainer =
    (kind: Drag["kind"], container: string, count: number) =>
    (e: DragEvent<HTMLElement>) => {
      if (drag?.kind !== kind) return;
      e.preventDefault();
      if (target?.container !== container)
        setTarget({ container, index: count });
    };
  const dropLine = (container: string, index: number) =>
    target?.container === container && target.index === index ? (
      <div className="layout-drop-line" aria-hidden="true" />
    ) : null;
  const fieldKeys =
    (field: Field, container: string, index: number) =>
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (container === "removed") return;
      const list = containerFields(container);
      const step = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = index + step;
      if (next < 0 || next >= list.length) return;
      focusHandle.current = `field:${field.id}`;
      placeField(
        field.id,
        container === "ungrouped"
          ? { kind: "ungrouped", index: next }
          : {
              kind: "section",
              groupId: container.slice("section:".length),
              index: next,
            },
      );
    };
  const sectionKeys =
    (id: string, column: number, index: number) =>
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const moves: Record<string, [number, number]> = {
        ArrowUp: [column, index - 1],
        ArrowDown: [column, index + 1],
        ArrowLeft: [column - 1, Number.MAX_SAFE_INTEGER],
        ArrowRight: [column + 1, Number.MAX_SAFE_INTEGER],
      };
      const move = moves[e.key];
      if (!move) return;
      e.preventDefault();
      const [nextColumn, nextIndex] = move;
      if (nextColumn < 1 || nextColumn > 3 || nextIndex < 0) return;
      if (
        nextColumn === column &&
        nextIndex >= sectionsInColumn(p, column).length
      )
        return;
      focusHandle.current = `section:${id}`;
      onChange(moveSection(p, id, nextColumn, nextIndex));
    };
  const fieldRow = (field: Field, container: string, index: number) => {
    const readOnly = !field.editable || field.system || !!field.calculation;
    const removed = container === "removed";
    return (
      <div
        key={field.id}
        className={`layout-field ${drag?.id === field.id ? "dragging" : ""} ${field.formDisplay === "hide" ? "is-hidden" : ""}`}
        draggable
        onDragStart={start("field", field.id)}
        onDragEnd={finish}
        onDragOver={overItem("field", container, index)}
      >
        <button
          type="button"
          className="drag-handle"
          data-handle={`field:${field.id}`}
          aria-label={`Move ${field.label}`}
          title={
            removed
              ? "Drag onto a section to add it back"
              : "Drag to move · Arrow keys reorder"
          }
          onKeyDown={fieldKeys(field, container, index)}
        >
          <GripVertical size={14} />
        </button>
        <div className="layout-field-text">
          <InlineText
            value={field.label}
            label={`${field.label} label`}
            onChange={(label) => updateField(field.id, { label })}
          />
          <small>
            {fieldTypeName(field.type)}
            {field.required && " · Required"}
            {readOnly && " · Read-only"}
            {field.formDisplay === "hide" && " · Hidden on form"}
          </small>
        </div>
        <div className="layout-field-actions">
          {removed ? (
            <button
              type="button"
              className="layout-text-action"
              aria-label={`Add ${field.label} to form`}
              onClick={() => updateField(field.id, { formDisplay: "show" })}
            >
              <Undo2 size={13} /> Add to form
            </button>
          ) : (
            <>
              <button
                type="button"
                className="icon-button"
                aria-label={`${field.formDisplay === "hide" ? "Show" : "Hide"} ${field.label} on form`}
                title={
                  field.formDisplay === "hide"
                    ? "Show on form"
                    : "Hide on form (keeps its value)"
                }
                onClick={() =>
                  updateField(field.id, {
                    formDisplay: field.formDisplay === "hide" ? "show" : "hide",
                  })
                }
              >
                {field.formDisplay === "hide" ? (
                  <EyeOff size={14} />
                ) : (
                  <Eye size={14} />
                )}
              </button>
              {container.startsWith("section:") && (
                <button
                  type="button"
                  className="icon-button"
                  aria-label={`Unassign ${field.label} from section`}
                  title="Take out of this section"
                  onClick={() =>
                    placeField(field.id, {
                      kind: "ungrouped",
                      index: Number.MAX_SAFE_INTEGER,
                    })
                  }
                >
                  <Undo2 size={14} />
                </button>
              )}
              <button
                type="button"
                className="icon-button"
                aria-label={`Remove ${field.label} from form`}
                title="Remove from form (keeps its value and design bindings)"
                onClick={() => placeField(field.id, { kind: "removed" })}
              >
                <X size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };
  const fieldList = (container: string, fields: Field[], empty: ReactNode) => (
    <div
      className={`layout-field-list ${target?.container === container ? "drop-target" : ""}`}
      onDragOver={overContainer("field", container, fields.length)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        drop();
      }}
    >
      {fields.map((field, index) => (
        <div key={field.id} className="layout-field-slot">
          {dropLine(container, index)}
          {fieldRow(field, container, index)}
        </div>
      ))}
      {dropLine(container, fields.length)}
      {!fields.length && <p className="layout-empty">{empty}</p>}
    </div>
  );
  return (
    <div className="form-layout-builder" ref={root}>
      <div className="layout-builder-toolbar">
        <label>
          Section layout
          <select
            aria-label="Form section layout"
            value={p.settings.formSectionLayout || "aligned"}
            onChange={(event) =>
              onChange({
                ...p,
                settings: {
                  ...p.settings,
                  formSectionLayout: event.target.value as
                    "aligned" | "stacked",
                },
              })
            }
          >
            <option value="aligned">Align horizontally</option>
            <option value="stacked">Stack tightly in columns</option>
          </select>
        </label>
        <p className="muted">
          Drag sections between columns and fields between sections. Click any
          name to rename it.
          {hasSections
            ? " Only fields inside a section appear on the form."
            : " Without sections, the form lists every field in order."}
        </p>
        {canUndoGenerated && (
          <button
            type="button"
            className="button"
            onClick={() => {
              onChange(restoreFormLayout(p, generated.before));
              setGenerated(undefined);
              setTabId("");
            }}
          >
            <Undo2 size={16} /> Undo generated layout
          </button>
        )}
        <button
          type="button"
          className="button"
          onClick={() => onChange(addSection(p, 1).project)}
        >
          <Plus size={16} />
          Add section
        </button>
      </div>
      {canUndoGenerated && (
        <p role="status" className="muted">
          Layout generated from your fields. Rename sections or drag fields to
          refine it. Calculated helpers are in a hidden section; existing field
          visibility is preserved.
        </p>
      )}
      <div className="layout-tabs" role="group" aria-label="Form tabs">
        <span className="layout-tabs-label">Tabs</span>
        {p.tabs.length > 0 && (
          <button
            type="button"
            className={`layout-tab ${tabId ? "" : "active"}`}
            aria-pressed={!tabId}
            onClick={() => setTabId("")}
          >
            All sections
          </button>
        )}
        {[...p.tabs]
          .sort((a, b) => a.order - b.order)
          .map((t) => (
            <span
              key={t.id}
              className={`layout-tab ${tabId === t.id ? "active" : ""}`}
            >
              {tabId === t.id ? (
                <>
                  <InlineText
                    value={t.name}
                    label="Tab name"
                    onChange={(name) =>
                      onChange({
                        ...p,
                        tabs: p.tabs.map((x) =>
                          x.id === t.id ? { ...x, name } : x,
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    className="layout-tab-delete"
                    aria-label={`Delete tab ${t.name}`}
                    onClick={() => {
                      setTabId("");
                      onChange({
                        ...p,
                        tabs: p.tabs.filter((x) => x.id !== t.id),
                      });
                    }}
                  >
                    <X size={12} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  aria-pressed={false}
                  onClick={() => setTabId(t.id)}
                >
                  {t.name || "Untitled tab"}
                  <small>{t.groupIds.length}</small>
                </button>
              )}
            </span>
          ))}
        <button
          type="button"
          className="layout-tab add"
          onClick={() => {
            const added = addTab(p);
            onChange(added.project);
            setTabId(added.id);
          }}
        >
          <Plus size={13} /> Add tab
        </button>
        {tab && (
          <span className="layout-tabs-hint">
            Use “Include in {tab.name}” on each section to choose what this tab
            shows.
          </span>
        )}
      </div>
      <div className="layout-builder-body">
        <div
          className="layout-builder-columns"
          style={{ "--layout-columns": visibleColumns.length } as CSSProperties}
        >
          {visibleColumns.map((column) => {
            const sections = sectionsInColumn(p, column),
              container = `column:${column}`;
            return (
              <div
                key={column}
                className={`layout-builder-column ${target?.container === container ? "drop-target" : ""}`}
                aria-label={`Column ${column}`}
                role="group"
                onDragOver={overContainer(
                  "section",
                  container,
                  sections.length,
                )}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  drop();
                }}
              >
                <span className="eyebrow">COLUMN {column}</span>
                {sections.map((g, index) => {
                  const inTab = !tab || tab.groupIds.includes(g.id);
                  // A tab shows only its own sections; the rest wait in the tray.
                  if (!inTab) return null;
                  return (
                    <div key={g.id} className="layout-section-slot">
                      {dropLine(container, index)}
                      <section
                        className={`layout-section ${drag?.id === g.id ? "dragging" : ""} ${g.visible ? "" : "is-hidden"} ${inTab ? "" : "outside-tab"}`}
                        aria-label={`Section ${g.name || "Untitled"}`}
                        onDragOver={overItem("section", container, index)}
                      >
                        <header
                          draggable
                          onDragStart={start("section", g.id)}
                          onDragEnd={finish}
                        >
                          <button
                            type="button"
                            className="drag-handle"
                            data-handle={`section:${g.id}`}
                            aria-label={`Move section ${g.name}`}
                            title="Drag to move · Arrow keys move between columns"
                            onKeyDown={sectionKeys(g.id, column, index)}
                          >
                            <GripVertical size={15} />
                          </button>
                          <InlineText
                            className="layout-section-name"
                            value={g.name}
                            label="Section name"
                            placeholder="Untitled section"
                            onChange={(name) => updateGroup(g.id, { name })}
                          />
                          <button
                            type="button"
                            className="icon-button"
                            aria-label={`${g.visible ? "Hide" : "Show"} section ${g.name}`}
                            title={g.visible ? "Hide section" : "Show section"}
                            onClick={() =>
                              updateGroup(g.id, { visible: !g.visible })
                            }
                          >
                            {g.visible ? (
                              <Eye size={14} />
                            ) : (
                              <EyeOff size={14} />
                            )}
                          </button>
                          <button
                            type="button"
                            className="icon-button danger"
                            aria-label={`Delete section ${g.name}`}
                            title="Delete section (its fields stay in the project)"
                            onClick={() => onChange(deleteSection(p, g.id))}
                          >
                            <Trash2 size={14} />
                          </button>
                        </header>
                        <InlineText
                          className="layout-section-note"
                          value={g.note}
                          allowEmpty
                          label={`${g.name} instructions`}
                          placeholder="Add instructions for this section"
                          onChange={(note) => updateGroup(g.id, { note })}
                        />
                        {tab && (
                          <label className="layout-tab-membership">
                            <input
                              type="checkbox"
                              checked={inTab}
                              onChange={(e) =>
                                onChange(
                                  setSectionInTab(
                                    p,
                                    tab.id,
                                    g.id,
                                    e.target.checked,
                                  ),
                                )
                              }
                            />
                            Include in {tab.name}
                          </label>
                        )}
                        {fieldList(
                          `section:${g.id}`,
                          fieldsInSection(p, g.id),
                          "Drag fields here",
                        )}
                        <select
                          className="layout-assign"
                          aria-label={`Assign field to ${g.name}`}
                          value=""
                          onChange={(e) =>
                            e.target.value &&
                            placeField(e.target.value, {
                              kind: "section",
                              groupId: g.id,
                              index: Number.MAX_SAFE_INTEGER,
                            })
                          }
                        >
                          <option value="">+ Add a field…</option>
                          {p.fields
                            .filter(
                              (f) =>
                                f.groupId !== g.id ||
                                f.formDisplay === "remove",
                            )
                            .map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.label}
                              </option>
                            ))}
                        </select>
                      </section>
                    </div>
                  );
                })}
                {dropLine(container, sections.length)}
                <button
                  type="button"
                  className="layout-add-section"
                  aria-label={`Add section to column ${column}`}
                  onClick={() => {
                    const added = addSection(p, column);
                    onChange(
                      tab
                        ? setSectionInTab(added.project, tab.id, added.id, true)
                        : added.project,
                    );
                  }}
                >
                  <Plus size={14} /> Add section
                </button>
              </div>
            );
          })}
        </div>
        <aside className="layout-builder-tray">
          {tab && (
            <section
              className="panel padded"
              aria-label={`Sections not in ${tab.name}`}
            >
              <h3>Not in {tab.name || "this tab"}</h3>
              <p className="muted">
                The tab shows only the columns its sections use, split evenly.
              </p>
              {outsideTab.length ? (
                <ul className="layout-outside-tab">
                  {outsideTab.map((g) => (
                    <li key={g.id}>
                      <span>
                        {g.name || "Untitled section"}
                        <small> · Column {g.column}</small>
                      </span>
                      <button
                        type="button"
                        className="button"
                        aria-label={`Include ${g.name} in ${tab.name}`}
                        onClick={() =>
                          onChange(setSectionInTab(p, tab.id, g.id, true))
                        }
                      >
                        <Plus size={13} /> Include
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">Every section is in this tab.</p>
              )}
            </section>
          )}
          <section className="panel padded" aria-label="Ungrouped form fields">
            <h3>{hasSections ? "Not in a section" : "Form fields"}</h3>
            <p className="muted">
              {hasSections
                ? "These won't appear on the form until you drag them into a section."
                : "Everything here appears on the form, in this order."}
            </p>
            {fieldList(
              "ungrouped",
              ungroupedFields(p),
              hasSections
                ? "Every field is in a section."
                : "No fields on the form yet.",
            )}
          </section>
          <section className="panel padded" aria-label="Available form fields">
            <h3>Removed from form</h3>
            <p className="muted">
              Drag a field here to take it off the form. Its value and design
              bindings are kept.
            </p>
            {fieldList("removed", removedFields(p), "Nothing removed.")}
          </section>
        </aside>
      </div>
    </div>
  );
}
